import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Users, ArrowLeft } from 'lucide-react';
import { PostCard } from '@/components/PostCard';
import { BottomNav } from '@/components/BottomNav';
import { CreatePostModal } from '@/components/CreatePostModal';

const FollowingPage = () => {
  const { user } = useContext(AppContext);
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchFollowingIdeas();
  }, []);

  const fetchFollowingIdeas = async () => {
    try {
      const response = await axios.get('/ideas/following');
      setIdeas(response.data);
    } catch (error) {
      toast.error('Failed to fetch ideas from following');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (ideaId, voteType) => {
    try {
      await axios.post(`/ideas/${ideaId}/vote`, { vote_type: voteType });
      fetchFollowingIdeas();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleCreatePost = async (formData) => {
    setIsSubmitting(true);
    try {
      await axios.post('/ideas', formData);
      toast.success('Idea posted successfully!');
      setIsModalOpen(false);
      fetchFollowingIdeas();
    } catch (error) {
      toast.error('Failed to post idea');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-8">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="bg-white border-b border-cardBorder sticky top-0 z-40"
      >
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-surface rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-grayDark" />
          </button>
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-baloo font-bold text-primaryDark">Following Feed</h1>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-lg mx-auto animate-spin" />
            <p className="text-gray mt-4 text-sm font-bold">Loading...</p>
          </div>
        ) : ideas.length === 0 ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-sm p-12 text-center border border-cardBorder"
          >
            <div className="w-20 h-20 bg-surface rounded-lg mx-auto mb-4 flex items-center justify-center">
              <Users className="w-10 h-10 text-grayMeta" />
            </div>
            <h3 className="font-baloo font-bold text-xl text-primaryDark mb-2">No posts yet</h3>
            <p className="text-gray mb-6">
              Follow users to see their ideas here!
            </p>
          </motion.div>
        ) : (
          ideas.map((idea, index) => (
            <PostCard key={idea.id} idea={idea} index={index} onVote={handleVote} currentUser={user} />
          ))
        )}
      </main>

      {/* Bottom Nav */}
      <BottomNav onCreateClick={() => setIsModalOpen(true)} />

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreatePost}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default FollowingPage;
